import { ApiResponse, PaginatedResponse } from "../types/common";
import {
  Department,
  CreateDepartmentRequest,
  UpdateDepartmentRequest,
  DepartmentStatistics,
} from "../types/department";
import api from "./api";

// Department CRUD Operations
export const getDepartments = async (
  page: number = 1,
  limit: number = 10,
  search?: string,
  branchId?: string,
  isActive?: boolean,
  includeStats?: boolean
): Promise<PaginatedResponse<Department>> => {
  const params = new URLSearchParams();

  params.append("page", page.toString());
  params.append("limit", limit.toString());

  if (search) params.append("search", search);
  if (branchId) params.append("branchId", branchId);
  if (isActive !== undefined) params.append("isActive", isActive.toString());
  if (includeStats) params.append("includeStats", includeStats.toString());

  const response = await api.get(`/departments?${params.toString()}`);
  // API interceptor returns response.data, so response is already the JSON data
  const data = response as any;

  return {
    success: data.success || true,
    data: data.departments || data.data || [],
    pagination: {
      currentPage: data.currentPage || data.pagination?.currentPage || 1,
      totalPages: data.totalPages || data.pagination?.totalPages || 1,
      totalItems: data.total || data.pagination?.totalItems || 0,
      hasNext:
        (data.currentPage || data.pagination?.currentPage || 1) <
        (data.totalPages || data.pagination?.totalPages || 1),
      hasPrev: (data.currentPage || data.pagination?.currentPage || 1) > 1,
    },
  };
};

export const getDepartmentById = async (
  id: string
): Promise<ApiResponse<Department>> => {
  const response = await api.get(`/departments/${id}`);
  // API interceptor returns response.data, so response is already the JSON data
  return response as any;
};

export const createDepartment = async (
  data: CreateDepartmentRequest
): Promise<ApiResponse<Department>> => {
  const response = await api.post("/departments", data);
  // API interceptor returns response.data, so response is already the JSON data
  return response as any;
};

export const updateDepartment = async (
  id: string,
  data: UpdateDepartmentRequest
): Promise<ApiResponse<Department>> => {
  const response = await api.put(`/departments/${id}`, data);
  // API interceptor returns response.data, so response is already the JSON data
  return response as any;
};

export const deleteDepartment = async (
  id: string
): Promise<ApiResponse<null>> => {
  const response = await api.delete(`/departments/${id}`);
  // API interceptor returns response.data, so response is already the JSON data
  return response as any;
};

export const getDepartmentStatistics = async (
  branchId?: string
): Promise<ApiResponse<DepartmentStatistics>> => {
  const params = new URLSearchParams();
  if (branchId) params.append("branchId", branchId);

  const response = await api.get(
    `/departments/statistics?${params.toString()}`
  );
  // API interceptor returns response.data, so response is already the JSON data
  return response as any;
};

// Get departments for a specific branch (used in dropdowns)
export const getDepartmentsByBranch = async (
  branchId: string
): Promise<ApiResponse<Department[]>> => {
  const response = await api.get(
    `/departments?branchId=${branchId}&isActive=true`
  );
  // API interceptor returns response.data, so response is already the JSON data
  const data = response as any;

  return {
    success: data.success || true,
    data: data.departments || data.data || [],
    message: data.message,
  };
};
